import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import "../ny.css";
import { buildAccentStyle } from "../game/stageShared";
import { setBackButtonUrl } from "../uiSlice";
import { nyAlert } from "../nyPopups";
import { fetchThemeData } from "../../admin/themeSlice";
import { getDragDropQuestions } from "../game/dragDropUtils";
import { QUIZ_MIN_QUESTIONS } from "../../admin/pages/themeupdate/quizHelpers";
import {
  getFirstSetupPath,
} from "../gameSetupStorage";

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);

  // Theme Button colors (Rules page) → PLAY button.
  const accentStyle = useMemo(() => buildAccentStyle(themeData), [themeData]);

  const handleBeginPlay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!user || beginLoading) return;

      setBeginLoading(true);
      try {
        let themePayload = themeData;
        try {
          const freshTheme = await dispatch(
            fetchThemeData({ themeId: null })
          ).unwrap();
          themePayload = freshTheme?.data ?? themeData;
        } catch (fetchErr) {
          console.error("handleBeginPlay theme fetch:", fetchErr);
        }

        // Same minimum the admin Quiz tab enforces (Game Content → Quiz). Only
        // playable rows count: question text, 2–4 options and a matching answer.
        const availableQuestions = getDragDropQuestions(themePayload);
        if (availableQuestions.length < QUIZ_MIN_QUESTIONS) {
          await nyAlert({
            kind: "warning",
            title: "Not enough questions",
            text: `This theme needs at least ${QUIZ_MIN_QUESTIONS} quiz questions configured (currently ${availableQuestions.length}). Add more in Admin → Game Content → Quiz.`,
          });
          return;
        }

        navigate(getFirstSetupPath(), { replace: true });
      } catch (err) {
        console.error("handleBeginPlay:", err);
        await nyAlert({
          kind: "error",
          title: "Unable to start",
          text: err?.message || "Something went wrong. Please try again.",
        });
      } finally {
        setBeginLoading(false);
      }
    },
    [user, beginLoading, navigate, dispatch, themeData]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    const body = document.body;
    const keys = [
      "background-attachment",
      "background-size",
      "background-repeat",
      "background-position",
      "overflow-x",
      "margin",
      "padding",
      "height",
      "font-family",
      "backdrop-filter",
    ];
    const snapshot = keys.map((k) => [k, body.style.getPropertyValue(k)]);

    body.style.setProperty("background-attachment", "fixed");
    body.style.setProperty("background-size", "100% 100%");
    body.style.setProperty("background-repeat", "no-repeat");
    body.style.setProperty("background-position", "center");
    body.style.setProperty("overflow-x", "hidden");
    body.style.setProperty("margin", "0");
    body.style.setProperty("padding", "0");
    body.style.setProperty("height", "100vh");
    body.style.setProperty(
      "font-family",
      "'FiraSans-Medium', sans-serif",
      "important"
    );
  

    return () => {
      snapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
    };
  }, []);

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  nyAlert({ kind: "error", title: "Session Expired!", text: msg }).then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

// show "already played" when login payload has gameover > 0 (DB stages.gameover)
useEffect(() => {
  if (isAdmin) return;
  if (status !== "succeeded" || !user) return;
  if (Number(user.gameover ?? 0) > 0) {
    const msg = error ? String(error) : "You have already played!";
    const redirect = user.backButtonRedirect || "/";
    nyAlert({ kind: "star", title: "You have already played!" }).then(() => {
      window.location.href = redirect;
    });
  }
}, [status, user, error, isAdmin]);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  // Start screen (reference screen 1): the theme's logo art + a big PLAY button.
  return (
    <div className="ny-login" style={accentStyle}>
      {themeData?.logo ? (
        <img
          src={process.env.REACT_APP_S3_PATH + themeData.logo}
          className="ny-login-logo"
          alt="Game logo"
        />
      ) : null}
      {user && status === "succeeded" && (
        <button
          type="button"
          className="ny-btn ny-btn--primary ny-btn--xl"
          disabled={beginLoading}
          onClick={handleBeginPlay}
        >
          {beginLoading ? "Loading…" : "PLAY"}
          {!beginLoading ? <i className="fa-solid fa-arrow-right" aria-hidden /> : null}
        </button>
      )}
    </div>
  );
}

export default Login;
